import { css } from "@strust/ui/css";
import { useMemo } from "react";
import { encode } from "uqr";

// Dark modules on a light square in both themes, so every authenticator app can scan it.
// Centred at its own size: a flex-column parent would otherwise stretch the frame and leave the
// code at its start.
const FRAME = css({
  display: "inline-flex",
  alignSelf: "center",
  justifyContent: "center",
  marginInline: "auto",
  p: "2",
  borderRadius: "md",
  bg: { base: "text", _light: "surface" },
  border: "1px solid",
  borderColor: "line",
});
const MODULES = css({ fill: { base: "bg", _light: "text" } });

export interface TotpQrCodeProps {
  /** The `otpauth://totp/...` URI of the authenticator to add (contains the secret: never logged). */
  readonly uri: string;
  /** Accessible name of the image. */
  readonly label: string;
  /** Rendered width in CSS pixels (default 192). */
  readonly size?: number;
}

/**
 * The QR code of an `otpauth://` URI, drawn here from the bundled encoder (design D5): no
 * third-party QR service, no network request, nothing kept after the component unmounts.
 */
export function TotpQrCode({ uri, label, size = 192 }: TotpQrCodeProps) {
  const path = useMemo(() => {
    const { data } = encode(uri, { ecc: "M", border: 0 });
    const parts: string[] = [];
    data.forEach((row, y) => {
      row.forEach((dark, x) => {
        if (dark) parts.push(`M${String(x)} ${String(y)}h1v1h-1z`);
      });
    });
    return { d: parts.join(""), modules: data.length };
  }, [uri]);
  return (
    <span className={FRAME}>
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox={`0 0 ${String(path.modules)} ${String(path.modules)}`}
        shapeRendering="crispEdges"
      >
        <path className={MODULES} d={path.d} />
      </svg>
    </span>
  );
}
